import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ConfiguracoesListagemFilterActions from './ListagemConfiguracao/ConfiguracoesListagemFilterActions';
import ListagemConfiguracaoModais from './ListagemConfiguracao/ListagemConfiguracaoModais';
import { ListagemConfiguracaoContextProvider } from './ListagemConfiguracao/ListagemConfiguracaoContext';
import { useListagemConfiguracao } from './ListagemConfiguracao/useListagemConfiguracao';
import { resolveConfiguracoesListagemEnabled } from '../utils/resolveConfiguracoesListagemEnabled';
import { Form } from 'react-bootstrap';
import styled, { createGlobalStyle } from 'styled-components';
import { Button, ButtonWithLoading } from './Button';
import Icon from './Icon';
import BottomStatusFilter from './BottomStatusFilter';
import SearchSelect from './form/SearchSelect';
import { DatePickerField } from './form/date-pickers/DatePicker';
import FormField from './form/FormField';
import MaskedInputField from './form/MaskedInput';
import { handleNumberInputWheel } from './form/numberInputUtils';
import {
  colorAccent,
  colorAdditional,
  colorBackgroundBody,
  colorBorder,
  colorFieldsBorder,
  colorText,
  colorWhite,
  colorHover,
  colorRed,
} from '../theme/palette';

/**
 * Componente de filtros reutilizável para listagens
 *
 * @param {Object} props
 * @param {Array} props.filters - Array de configurações de filtros
 * @param {Function} props.onApply - Função chamada ao aplicar filtros
 * @param {boolean} props.loading - Estado de carregamento
 * @param {boolean} props.collapsible - Se o painel pode ser colapsado
 * @param {boolean} props.defaultCollapsed - Se deve iniciar colapsado
 * @param {boolean} props.enableAdvancedFilters - Se deve separar filtros principais e avancados
 * @param {string} props.advancedLabel - Texto do botao que abre os filtros avancados
 * @param {boolean} props.advancedFiltersDefaultExpanded - Se os filtros avancados devem iniciar abertos
 * @param {string} props.title - Título do painel de filtros
 */

const emptyValues = {};

const areObjectsShallowEqual = (current, next) => {
  const currentKeys = Object.keys(current || {});
  const nextKeys = Object.keys(next || {});

  if (currentKeys.length !== nextKeys.length) return false;

  return nextKeys.every(key => current?.[key] === next?.[key]);
};

const hasFilterValue = (value) => {
  if (Array.isArray(value)) return value.some(hasFilterValue);
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') {
    if ('value' in value) return hasFilterValue(value.value);
    return Object.keys(value).length > 0;
  }
  return true;
};

const FilterPanelCore = ({
  filters = [],
  values = emptyValues,
  onApply,
  loading = false,
  collapsible = true,
  defaultCollapsed = false,
  collapseOnSearch = false,
  title = 'Filtros',
  applyLabel = 'Pesquisar',
  applyLoadingLabel = 'Pesquisando...',
  clearLabel = 'Limpar',
  clearLoadingLabel = 'Limpando...',
  enableAdvancedFilters = false,
  advancedLabel = 'Avançado',
  advancedFiltersDefaultExpanded = false,
  leadingActions = null,
  onLocalValuesChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(advancedFiltersDefaultExpanded);
  const [localValues, setLocalValues] = useState({}); // Inicializar valores locais com valores padrão dos filtros

  useEffect(() => {
    const initialValues = {};
    filters.forEach((filter) => {
      initialValues[filter.id] = filter.defaultValue ?? '';
    });
    const nextValues = {
      ...initialValues,
      ...(values || {}),
    };

    setLocalValues(currentValues => (areObjectsShallowEqual(currentValues, nextValues) ? currentValues : nextValues));
  }, [filters, values]);

  // Expoe o rascunho do painel (valores digitados, mesmo sem "Pesquisar")
  // para consumidores como o salvar configuracao de listagem.
  useEffect(() => {
    onLocalValuesChange?.(localValues);
  }, [localValues, onLocalValuesChange]);

  const handleInputChange = useCallback((name, value) => {
    setLocalValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSearch = useCallback(() => {
    if (onApply) {
      onApply(localValues);
    }

    if (collapseOnSearch && collapsible) {
      setIsCollapsed(true);
    }
  }, [onApply, localValues, collapseOnSearch, collapsible]);

  const handleClear = useCallback(() => {
    const clearedValues = {};
    filters.forEach((filter) => {
      clearedValues[filter.id] = filter.defaultValue ?? '';
    });
    setLocalValues(clearedValues);

    if (onApply) {
      onApply(clearedValues);
    }
  }, [filters, onApply]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const renderFilter = useCallback(
    (filter) => {
      const {
        id,
        type,
        placeholder,
        options = [],
        mask,
        guide = false,
        sanitizeValue,
        label: filterLabel,
        ...rest
      } = filter;
      const value = localValues[id] ?? '';
      const getSanitizedValue = nextValue => (typeof sanitizeValue === 'function' ? sanitizeValue(nextValue) : nextValue);
      const label = filterLabel ? (
        <FilterLabelContent>
          {hasFilterValue(value) && <ActiveFilterIndicator aria-hidden="true" />}
          <FilterLabelText>{filterLabel}</FilterLabelText>
        </FilterLabelContent>
      ) : filterLabel;

      switch (type) {
        case 'searchSelect':
          return (
            <div className="filter-panel-searchselect">
              <SearchSelect
                name={id}
                value={value || null}
                onChange={(selectedOption) => {
                  handleInputChange(id, selectedOption || '');
                }}
                placeholder={placeholder}
                options={options}
                label={label}
                {...rest}
              />
            </div>
          );

        case 'datePicker': {
          const isDateTime = rest?.pickerType === 'datetime' || rest?.showTimeSelect;

          return (
            <FormField
              component={DatePickerField}
              name={id}
              value={value || null}
              onChange={(date) => {
                if (date) {
                  // Converter Date para string YYYY-MM-DD (ou com hora)
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');

                  let dateString = `${year}-${month}-${day}`;

                  if (isDateTime) {
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    dateString = `${dateString} ${hours}:${minutes}`;
                  }

                  handleInputChange(id, dateString);
                } else {
                  handleInputChange(id, '');
                }
              }}
              label={label}
              placeholder={placeholder}
              inputClassName="filter-input"
              wrapperClassName="filter-panel-datepicker"
              {...rest}
            />
          );
        }

        case 'masked':
          return (
            <MaskedInputField
              name={id}
              value={value}
              onChange={(newValue) => {
                handleInputChange(id, newValue);
              }}
              placeholder={placeholder}
              mask={mask}
              guide={guide}
              label={label}
              {...rest}
            />
          );

        case 'bottomStatusFilter':
          return (
            <BottomStatusFilter
              options={options}
              value={value}
              onChange={(selectedValue) => {
                const nextValues = { ...localValues, [id]: selectedValue };
                setLocalValues(nextValues);
                if (onApply) {
                  onApply(nextValues);
                }
              }}
            />
          );

        case 'select': {
          // Remover defaultValue dos props para evitar warning de controlado/uncontrolado
          const { defaultValue, ...selectProps } = rest;

          return (
            <Form.Select
              name={id}
              value={value}
              onChange={(e) => handleInputChange(id, e.target.value)}
              className="filter-input"
              {...selectProps}
            >
              <option value="">{placeholder || 'Selecione...'}</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          );
        }

        case 'date':
          return (
            <Form.Control
              name={id}
              type="date"
              value={value}
              onChange={(e) => handleInputChange(id, e.target.value)}
              className="filter-input"
              placeholder={placeholder}
              {...rest}
            />
          );

        case 'number':
          return (
            <Form.Control
              name={id}
              type="number"
              value={value}
              onChange={(e) => handleInputChange(id, getSanitizedValue(e.target.value))}
              onWheel={handleNumberInputWheel}
              className="filter-input"
              placeholder={placeholder}
              {...rest}
            />
          );

        case 'checkbox':
          return (
            <Form.Check
              name={id}
              type="checkbox"
              checked={value === true}
              onChange={(e) => handleInputChange(id, e.target.checked)}
              {...rest}
            />
          );

        default:
          return (
            <Form.Control
              name={id}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(id, getSanitizedValue(e.target.value))}
              className="filter-input"
              placeholder={placeholder}
              {...rest}
            />
          );
      }
    },
    [localValues, handleInputChange]
  );

  const shouldRenderFilters = !collapsible || !isCollapsed;
  const splitFilters = enableAdvancedFilters
    ? filters.reduce((acc, filter) => {
      const isAdvanced = typeof filter.advanced === 'boolean'
        ? filter.advanced
        : !hasFilterValue(filter.defaultValue);

      if (isAdvanced) {
        acc.advancedFilters.push(filter);
      } else {
        acc.primaryFilters.push(filter);
      }

      return acc;
    }, {
      primaryFilters: [],
      advancedFilters: [],
    })
    : {
      primaryFilters: filters,
      advancedFilters: [],
    };
  const hasAdvancedFilters = splitFilters.advancedFilters.length > 0;
  const activeAdvancedFiltersCount = splitFilters.advancedFilters
    .filter(filter => hasFilterValue(localValues[filter.id]))
    .length;
  const renderFilterField = (filter, index) => (
    <FilterField key={`${filter.id}-${index}`} fullWidth={filter.fullWidth}>
      {filter.label &&
        filter.type !== 'searchSelect' &&
        filter.type !== 'datePicker' &&
        filter.type !== 'masked' &&
        filter.type !== 'bottomStatusFilter' &&
        filter.type !== 'checkbox' && (
          <FilterLabel>
            <FilterLabelContent>
              {hasFilterValue(localValues[filter.id]) && <ActiveFilterIndicator aria-hidden="true" />}
              <FilterLabelText>{filter.label}</FilterLabelText>
            </FilterLabelContent>
          </FilterLabel>
        )}
      {renderFilter(filter)}
    </FilterField>
  );

  return (
    <FilterPanelContainer>
      <FilterPanelGlobalStyles />
      <FilterHeader onClick={collapsible ? toggleCollapse : undefined}>
        <FilterTitle>
          <Icon name="filter" size={16} />
          <span>{title}</span>
          {filters.length > 0 && <FilterCount>({filters.length} filtros)</FilterCount>}
        </FilterTitle>

        {collapsible && (
          <CollapseButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
            collapsed={isCollapsed}
            title={isCollapsed ? 'Expandir filtros' : 'Recolher filtros'}
          >
            <Icon name={isCollapsed ? 'chevron-down' : 'chevron-up'} size={16} />
          </CollapseButton>
        )}
      </FilterHeader>

      <FilterContent collapsed={isCollapsed}>
        {shouldRenderFilters && (
          <>
            <FilterGrid>
              {splitFilters.primaryFilters.map(renderFilterField)}
            </FilterGrid>

            {hasAdvancedFilters && (
              <AdvancedFiltersArea $expanded={showAdvancedFilters} aria-hidden={!showAdvancedFilters}>
                <FilterGrid>
                  {splitFilters.advancedFilters.map(renderFilterField)}
                </FilterGrid>
              </AdvancedFiltersArea>
            )}

            <FilterActions $hasLeadingActions={!!leadingActions}>
              {leadingActions && (
                <FilterActionsStart>
                  {leadingActions}
                </FilterActionsStart>
              )}

              <FilterActionsEnd>
                {hasAdvancedFilters && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => setShowAdvancedFilters(prev => !prev)}
                  >
                    <Icon name={showAdvancedFilters ? 'chevron-up' : 'chevron-down'} size={14} />
                    <span>
                      {showAdvancedFilters ? 'Ocultar avançado' : advancedLabel}
                      {activeAdvancedFiltersCount > 0 ? ` (${activeAdvancedFiltersCount})` : ''}
                    </span>
                  </Button>
                )}

                <ButtonWithLoading
                  variant="primary"
                  size="sm"
                  onClick={handleSearch}
                  disabled={loading}
                  loading={loading}
                >
                  <Icon name="search" size={14} />
                  <span>{loading ? applyLoadingLabel : applyLabel}</span>
                </ButtonWithLoading>

                <ButtonWithLoading
                  variant="secondary"
                  size="sm"
                  onClick={handleClear}
                  disabled={loading}
                  loading={loading}
                >
                  <Icon name="refresh" size={14} />
                  <span>{loading ? clearLoadingLabel : clearLabel}</span>
                </ButtonWithLoading>
              </FilterActionsEnd>
            </FilterActions>
          </>
        )}
      </FilterContent>
    </FilterPanelContainer>
  );
};

const listagemConfigShape = PropTypes.shape({
  rotaListagem: PropTypes.string.isRequired,
  columnConfigKey: PropTypes.string.isRequired,
  colunas: PropTypes.shape({
    todasIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    sempreVisiveis: PropTypes.arrayOf(PropTypes.string).isRequired,
    padraoVisiveis: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  normalizeFilterValue: PropTypes.func,
});

FilterPanelCore.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf([
        'text',
        'select',
        'date',
        'number',
        'checkbox',
        'searchSelect',
        'datePicker',
        'masked',
        'bottomStatusFilter',
      ]),
      label: PropTypes.node,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.string,
        })
      ),
      defaultValue: PropTypes.any,
      mask: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      guide: PropTypes.bool,
      fullWidth: PropTypes.bool,
      advanced: PropTypes.bool,
    })
  ),
  values: PropTypes.object,
  onApply: PropTypes.func,
  loading: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  collapseOnSearch: PropTypes.bool,
  title: PropTypes.string,
  applyLabel: PropTypes.string,
  applyLoadingLabel: PropTypes.string,
  clearLabel: PropTypes.string,
  clearLoadingLabel: PropTypes.string,
  enableAdvancedFilters: PropTypes.bool,
  advancedLabel: PropTypes.string,
  advancedFiltersDefaultExpanded: PropTypes.bool,
  leadingActions: PropTypes.node,
  onLocalValuesChange: PropTypes.func,
};

const FilterPanel = ({
  configuracoesListagem = false,
  listagemConfig,
  onApplyConfiguracao,
  onConfiguracaoReady,
  podeSalvarGeral = false,
  children,
  leadingActions = null,
  filters = [],
  values = emptyValues,
  ...rest
}) => {
  const isConfigEnabled = configuracoesListagem === true
    && resolveConfiguracoesListagemEnabled();
  const hasRequiredConfigProps = !!listagemConfig && typeof onApplyConfiguracao === 'function';

  if (configuracoesListagem === true && !hasRequiredConfigProps && process.env.NODE_ENV !== 'production') {
    console.error('FilterPanel: configuracoesListagem requer listagemConfig e onApplyConfiguracao');
  }

  // Rascunho atual do painel: garante que salvar configuracao capture os
  // valores preenchidos mesmo sem o usuario ter clicado em "Pesquisar".
  const draftValuesRef = React.useRef(values);
  const handleLocalValuesChange = useCallback((nextValues) => {
    draftValuesRef.current = nextValues;
  }, []);
  const obterValoresAtuais = useCallback(() => draftValuesRef.current, []);

  const listagem = useListagemConfiguracao({
    listagemConfig,
    filters,
    values,
    enabled: isConfigEnabled && hasRequiredConfigProps,
    podeSalvarGeral,
    onApplyConfiguracao,
    onConfiguracaoReady,
    obterValoresAtuais
  });

  const resolvedLeadingActions = isConfigEnabled && hasRequiredConfigProps
    ? (
      <>
        <ConfiguracoesListagemFilterActions
          onAbrirConfiguracoes={listagem.abrirListagemModal}
          onSalvarConfiguracao={listagem.abrirSalvarModal}
          loading={listagem.loadingPadrao}
          podeSalvarGeral={podeSalvarGeral}
        />
        {leadingActions}
      </>
    )
    : leadingActions;

  const panel = (
    <FilterPanelCore
      {...rest}
      filters={filters}
      values={values}
      leadingActions={resolvedLeadingActions}
      onLocalValuesChange={handleLocalValuesChange}
    />
  );

  if (!isConfigEnabled || !hasRequiredConfigProps) {
    return (
      <>
        {panel}
        {children}
      </>
    );
  }

  return (
    <>
      <ListagemConfiguracaoContextProvider value={listagem.contextValue}>
        {panel}
        {children}
      </ListagemConfiguracaoContextProvider>
      <ListagemConfiguracaoModais {...listagem.modalProps} />
    </>
  );
};

FilterPanel.propTypes = {
  ...FilterPanelCore.propTypes,
  configuracoesListagem: PropTypes.bool,
  listagemConfig: listagemConfigShape,
  onApplyConfiguracao: PropTypes.func,
  onConfiguracaoReady: PropTypes.func,
  podeSalvarGeral: PropTypes.bool,
  children: PropTypes.node,
};

const FilterPanelContainer = styled.div`
  background: ${colorWhite};
  border: 1px solid ${colorBorder};
  border-radius: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: visible;
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: ${colorHover};
  border-bottom: 1px solid ${colorBorder};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colorBackgroundBody};
  }

  @media (max-width: 576px) {
    padding: 10px 12px;
  }
`;

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${colorText};
  font-size: 14px;

  @media (max-width: 576px) {
    font-size: 12px;
    gap: 6px;
  }
`;

const FilterCount = styled.span`
  font-size: 12px;
  color: ${colorAdditional};
  font-weight: 400;

  @media (max-width: 576px) {
    font-size: 11px;
  }
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${colorAdditional};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${colorWhite};
    color: ${colorText};
    transform: scale(1.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${colorAccent}40;
  }

  @media (max-width: 576px) {
    padding: 4px;
  }
`;

const FilterContent = styled.div`
  max-height: ${(props) => (props.collapsed ? '0px' : '5000px')};
  opacity: ${(props) => (props.collapsed ? '0' : '1')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, padding 0.3s ease-in-out;
  padding: ${(props) => (props.collapsed ? '0 20px' : '20px')};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (max-width: 576px) {
    gap: 10px;
  }
`;

const AdvancedFiltersArea = styled.div`
  max-height: ${({ $expanded }) => ($expanded ? '5000px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? '1' : '0')};
  visibility: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
  overflow: hidden;
  padding-top: ${({ $expanded }) => ($expanded ? '18px' : '0')};
  margin-top: ${({ $expanded }) => ($expanded ? '2px' : '0')};
  border-top: 1px solid ${({ $expanded }) => ($expanded ? colorBorder : 'transparent')};
  transform: translateY(${({ $expanded }) => ($expanded ? '0' : '-6px')});
  pointer-events: ${({ $expanded }) => ($expanded ? 'auto' : 'none')};
  transition:
    max-height 0.35s ease-in-out,
    opacity 0.22s ease-in-out,
    transform 0.25s ease-in-out,
    padding-top 0.25s ease-in-out,
    margin-top 0.25s ease-in-out,
    border-color 0.25s ease-in-out,
    visibility 0.25s ease-in-out;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${(props) =>
    props.fullWidth &&
    `
    grid-column: 1 / -1;
  `}
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${colorText};
  margin-bottom: 2px;
`;

const FilterLabelContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

const ActiveFilterIndicator = styled.i`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  border-radius: 50%;
  background: ${colorRed};
  box-shadow: 0 0 0 2px rgba(232, 25, 32, 0.12);
`;

const FilterLabelText = styled.b`
  color: inherit;
  font-weight: inherit;
  min-width: 0;
`;

const FilterActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: ${({ $hasLeadingActions }) => ($hasLeadingActions ? 'space-between' : 'flex-end')};
  align-items: center;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    justify-content: center;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterActionsStart = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;

    & > * {
      width: 100%;
    }
  }
`;

const FilterActionsEnd = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  margin-left: auto;

  & > * {
    flex: 0 1 auto;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;
    margin-left: 0;

    & > * {
      width: 100%;
    }
  }
`;

// Estilos globais para os inputs
const FilterPanelGlobalStyles = createGlobalStyle`
  .filter-input {
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    height: 40px;
    transition: all 0.3s ease;
    background: ${colorWhite};
    border: 1px solid ${colorFieldsBorder};
    border-radius: 0;
    color: ${colorText};

    &::placeholder {
      color: ${colorAdditional};
    }

    &:focus {
      outline: none;
      border-color: ${colorAccent};
      box-shadow: 0 0 0 0.2rem rgba(35, 57, 128, 0.15);
      background: ${colorWhite};
    }

    &:hover {
      border-color: ${colorAccent};
      background: ${colorHover};
    }

    &:disabled {
      background: ${colorBackgroundBody};
      color: ${colorAdditional};
      cursor: not-allowed;

      &:focus, &:active {
        border-color: ${colorBorder};
        box-shadow: none;
      }
    }

    @media (max-width: 576px) {
      height: 44px;
      font-size: 16px; /* Evita zoom no iOS */
    }
  }

  /* Estilos específicos para DatePicker no FilterPanel */
  .filter-panel-datepicker {
    width: 100%;

    /* Estilos para o wrapper do DatePickers */
    > div {
      width: 100%;
    }

    .react-datepicker-wrapper {
      width: 100%;
    }

    .react-datepicker__input-container {
      width: 100%;
    }

    .react-datepicker__input-container input {
      width: 100%;
      height: 40px;
      padding: 8px 12px;
      border: 1px solid ${colorFieldsBorder};
      border-radius: 0;
      font-size: 14px;
      line-height: 1.5;
      background: ${colorWhite};
      transition: all 0.3s;
    }

    .react-datepicker__input-container input:focus {
      outline: none;
      border-color: ${colorAccent};
      box-shadow: 0 0 0 0.2rem rgba(35, 57, 128, 0.15);
    }

    .react-datepicker__input-container input:hover {
      border-color: ${colorAccent};
      background: ${colorHover};
    }

    .react-datepicker__input-container input:disabled {
      background: ${colorBackgroundBody};
      color: ${colorAdditional};
      cursor: not-allowed;
      box-shadow: none;
    }

    /* Garantir que o FormGroup tenha o layout correto */
    .form-group {
      margin-bottom: 0;
      width: 100%;
    }

    /* Garantir que a label tenha o estilo correto */
    .form-group-label {
      font-size: 12px;
      font-weight: 500;
      color: ${colorText};
      margin-bottom: 4px;
      display: block;
    }
  }

  /* Estilos específicos para SearchSelect no FilterPanel */
  .filter-panel-searchselect {
    width: 100%;

    .react-select__control {
      height: 40px;
      border-radius: 0 !important;
      transition: all 0.3s;
      border: 1px solid ${colorFieldsBorder};
      background-color: ${colorWhite};

      &.react-select__control--is-focused, &:hover {
        border-color: ${colorAccent} !important;
        box-shadow: 0 0 0 0.2rem rgba(35, 57, 128, 0.15);
        background: ${colorWhite};
      }
    }

    @media (max-width: 576px) {
      .react-select__control {
        height: 44px;
      }
    }
  }
`;

export default FilterPanel;
